// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetWishListsApiParameter } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadWishLists from "@insite/client-framework/Store/Data/WishLists/Handlers/LoadWishLists";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import { getWishListsDataView } from "@insite/client-framework/Store/Data/WishLists/WishListsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@synd/App/Components/Notify";
import Skeleton from "@synd/App/Components/Skeleton/Skeleton";
import TitledSection, { CardWrapperStyles } from "@synd/App/Components/TitledSection/TitledSection";
import WishListCard, { WishListCardStyles } from "@synd/Overrides/Components/WishListCard";
import WidgetLists from "@synd/XDL/Icons/WidgetLists";
import React, { Fragment } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    numberOfRecords = "numberOfRecords",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        recentWishListsDataView: getWishListsDataView(state, recentWishListsParameter),
        myListsPageNavLink: getPageLinkByPageType(state, "MyListsPage"),
        canViewWishLists: (session.isAuthenticated || session.rememberMe) && !session.isGuest,
    };
};

let recentWishListsParameter = {
    page: 1,
    sort: "ModifiedOn DESC",
    expand: ["top3products", "favorite", "hidden", "tags"],
    wishListLinesSort: "mostRecent",
} as GetWishListsApiParameter;

const mapDispatchToProps = {
    loadWishLists,
    addWishListToCart,
    updateWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentWishListsStyles {
    wishListCardGridItem?: GridItemProps;
    noWishListsText?: TypographyProps;
    wishListCard?: WishListCardStyles;
    // XNG-Changes
    wishListCardsContainer?: InjectableCss;
    skeletonWrapper?: InjectableCss;
    titledSection?: CardWrapperStyles;
}

export const recentWishListsStyles: RecentWishListsStyles = {
    wishListCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
        `,
    },
    wishListCard: {
        actionAddToCartButton: {
            typographyProps: {
                size: 14,
            },
        },
    },
};

const styles = recentWishListsStyles;

class RecentWishLists extends React.Component<Props> {
    componentDidMount() {
        recentWishListsParameter.pageSize = this.props.fields.numberOfRecords;
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    componentDidUpdate() {
        if (
            this.props.canViewWishLists &&
            !this.props.recentWishListsDataView.value &&
            !this.props.recentWishListsDataView.isLoading
        ) {
            this.props.loadWishLists(recentWishListsParameter);
        }
    }

    // toggleFavoriteClickHandler = (wishList: WishListModel) => {
    //     if (!wishList) {
    //         return;
    //     }

    //     this.props.updateWishList({
    //         apiParameter: {
    //             wishList: {
    //                 ...wishList,
    //                 isFavorite: !wishList.isFavorite,
    //                 wishListLineCollection: null,
    //             },
    //         },
    //         onComplete(resultProps) {
    //             if (resultProps.result?.wishList) {
    //                 this.onSuccess?.();
    //             } else if (resultProps.result?.errorMessage) {
    //                 this.onError?.(resultProps.result.errorMessage);
    //             }
    //         },
    //     });
    // };

    // toggleHideClickHandler = (wishList: WishListModel) => {
    //     if (!wishList) {
    //         return;
    //     }

    //     this.props.updateWishList({
    //         apiParameter: {
    //             wishList: {
    //                 ...wishList,
    //                 isHidden: !wishList.isHidden,
    //                 wishListLineCollection: null,
    //             },
    //         },
    //         onComplete(resultProps) {
    //             if (resultProps.result?.wishList) {
    //                 this.onSuccess?.();
    //             } else if (resultProps.result?.errorMessage) {
    //                 this.onError?.(resultProps.result.errorMessage);
    //             }
    //         },
    //     });
    // };

    render() {
        if (!this.props.canViewWishLists) {
            return null;
        }

        const {
            recentWishListsDataView: { value: recentWishLists, isLoading },
            myListsPageNavLink,
        } = this.props;
        const myListsPageUrl = myListsPageNavLink ? myListsPageNavLink.url : undefined;

        // XNG-Changes: Changed structure
        return (
            <TitledSection
                data-test-selector="cardListRecentWishLists"
                icon={WidgetLists}
                title={translate("Lists")}
                buttonText={translate("View All")}
                buttonUrl={myListsPageUrl}
                extendedStyles={styles.titledSection}
            >
                {recentWishLists && recentWishLists.length === 0 && (
                    <Notify {...styles.noWishListsText} type="info">
                        {siteMessage("WishLists_NoWishlistsFound")}
                    </Notify>
                )}

                {/* XNG-Changes: Added skeleton */}
                {isLoading &&
                    Array.from({ length: 3 }, (_, index) => (
                        <StyledWrapper key={index} {...styles.skeletonWrapper}>
                            <Skeleton
                                width="52px"
                                height="52px"
                                borderRadius="50%"
                                css={css`
                                    grid-row: 1 / span 2;
                                `}
                            />
                            <Skeleton width="45%" height="17px" />
                            <Skeleton width="100%" height="17px" />
                        </StyledWrapper>
                    ))}

                {recentWishLists?.map(wishList => (
                    <Fragment key={wishList.id}>
                        <WishListCard wishList={wishList} extendedStyles={styles.wishListCard} isMiniView />
                    </Fragment>
                ))}
            </TitledSection>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentWishLists),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Lists Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 3,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
